import { useEffect, useRef } from 'react';

/**
 * «Atrás» del móvil cierra lo último que se abrió (hoja, ventana, teclado,
 * brújula) en vez de cambiar de pantalla. Cada cosa abierta añade al
 * historial una entrada con la misma URL y entra en una pila: un «atrás»
 * cierra solo la de arriba, así una hoja abierta encima de otra se cierra
 * primero.
 *
 * Si se cierra por otra vía (✕, velo, deslizar, opción elegida) se quita su
 * entrada para no dejar un «atrás» muerto. Las entradas que quedaron sin
 * dueño (p. ej. una opción de la hoja cambió de pantalla) se saltan solas.
 *
 * Mientras un «atrás» nuestro va de camino, lo que se abra espera a que
 * llegue para añadir su entrada: si no, el navegador se la llevaría por
 * delante y la ventana nueva se cerraría sola.
 */

const MARCA = 'rumboAtras';

/** Si «atrás» no llega a tiempo, lo pendiente se hace igualmente. */
const ESPERA_MAXIMA_MS = 400;

type Entrada = { id: string; cerrar: () => void; empujada: boolean };

const pila: Entrada[] = [];
/** «Atrás» lanzados por nosotros: su `popstate` no cierra nada. */
let propios = 0;
let pendientes: Array<() => void> = [];
/** Reserva del último «atrás» propio; uno viejo no adelanta lo de otro. */
let reserva: number | undefined;
let escuchando = false;
let contador = 0;
/** El próximo `popstate` es un salto nuestro entre pantallas: no se toca. */
let quieto = false;

function marcaActual(): string | null {
  const estado: unknown = window.history.state;
  if (typeof estado !== 'object' || estado == null) {
    return null;
  }
  const marca = (estado as Record<string, unknown>)[MARCA];
  return typeof marca === 'string' ? marca : null;
}

function ejecutarPendientes() {
  window.clearTimeout(reserva);
  reserva = undefined;
  const lista = pendientes;
  pendientes = [];
  for (const accion of lista) {
    accion();
  }
}

/** Lanza un «atrás» propio; `luego` va cuando ya ha llegado. */
function atrasPropio(luego?: () => void) {
  propios += 1;
  if (luego) {
    pendientes.push(luego);
  }
  window.clearTimeout(reserva);
  reserva = window.setTimeout(ejecutarPendientes, ESPERA_MAXIMA_MS);
  window.history.back();
}

/** ¿Estamos en una entrada de algo que ya no está abierto? */
function enHuerfana(): boolean {
  const marca = marcaActual();
  return marca != null && !pila.some(e => e.id === marca);
}

function alVolver() {
  if (quieto) {
    quieto = false;
    return;
  }
  if (propios > 0) {
    propios -= 1;
    if (propios === 0 && enHuerfana()) {
      // Seguimos saltando: la de debajo también quedó sin dueño.
      atrasPropio();
      return;
    }
    ejecutarPendientes();
    return;
  }
  const arriba = pila.pop();
  if (arriba) {
    arriba.cerrar();
    return;
  }
  // Entrada de una hoja que ya no existe: «atrás» otra vez para no gastar
  // un toque sin que pase nada.
  if (enHuerfana()) {
    atrasPropio();
  }
}

function escuchar() {
  if (!escuchando) {
    escuchando = true;
    window.addEventListener('popstate', alVolver);
  }
}

function empujar(entrada: Entrada) {
  if (entrada.empujada || !pila.includes(entrada)) {
    return;
  }
  entrada.empujada = true;
  window.history.pushState(
    { ...window.history.state, [MARCA]: entrada.id },
    '',
  );
}

export type EntradaAtras = {
  /**
   * Quita la entrada (al cerrar por otra vía que no sea «atrás»). `luego`
   * se ejecuta cuando el historial ya ha vuelto a su sitio, para que una
   * opción que navega no lo haga encima de la entrada.
   */
  quitar: (luego?: () => void) => void;
};

/** Registra algo abierto. `cerrar` se llama si el usuario pulsa «atrás». */
export function abrirEntradaAtras(cerrar: () => void): EntradaAtras {
  escuchar();
  contador += 1;
  const id = `${Date.now().toString(36)}-${contador}`;
  const entrada: Entrada = { id, cerrar, empujada: false };
  pila.push(entrada);
  if (propios > 0) {
    pendientes.push(() => empujar(entrada));
  } else {
    empujar(entrada);
  }
  return {
    quitar(luego) {
      const i = pila.indexOf(entrada);
      if (i < 0) {
        // Ya se cerró con «atrás».
        luego?.();
        return;
      }
      pila.splice(i, 1);
      if (entrada.empujada && marcaActual() === id) {
        atrasPropio(luego);
        return;
      }
      if (luego) {
        // Si hay un «atrás» nuestro de camino, después de él.
        if (propios > 0) {
          pendientes.push(luego);
        } else {
          luego();
        }
      }
    },
  };
}

/**
 * El próximo «atrás» (o salto en el historial) lo lanza la app para cambiar
 * de pantalla: no cierra nada ni salta entradas.
 */
export function saltoPropio() {
  escuchar();
  quieto = true;
}

/**
 * Cierra todo lo abierto y quita sus entradas; `luego` va cuando el
 * historial ya está limpio. Para cambiar de pestaña con algo abierto.
 */
export function cerrarTodoConAtras(luego: () => void) {
  const abiertas = pila.splice(0);
  for (const entrada of abiertas.reverse()) {
    entrada.cerrar();
  }
  if (propios > 0) {
    // Ya hay un «atrás» nuestro de camino (p. ej. la brújula se cerró).
    pendientes.push(luego);
  } else if (enHuerfana()) {
    atrasPropio(luego);
  } else {
    luego();
  }
}

/**
 * Mientras `abierto`, «atrás» llama a `cerrar`. Devuelve
 * `cerrarYLuego(accion)`: cierra y ejecuta `accion` cuando el historial ya
 * ha vuelto a su sitio.
 */
export function useCerrarConAtras(abierto: boolean, cerrar: () => void) {
  const cerrarRef = useRef(cerrar);
  useEffect(() => {
    cerrarRef.current = cerrar;
  }, [cerrar]);
  const entradaRef = useRef<EntradaAtras | null>(null);

  useEffect(() => {
    if (!abierto) {
      return;
    }
    const entrada = abrirEntradaAtras(() => {
      entradaRef.current = null;
      cerrarRef.current();
    });
    entradaRef.current = entrada;
    return () => {
      if (entradaRef.current === entrada) {
        entradaRef.current = null;
        entrada.quitar();
      }
    };
  }, [abierto]);

  return (accion?: () => void) => {
    const entrada = entradaRef.current;
    entradaRef.current = null;
    cerrarRef.current();
    if (entrada) {
      entrada.quitar(accion);
    } else {
      accion?.();
    }
  };
}

/**
 * Asistentes por pasos: mientras `paso` no sea `null`, «atrás» llama a
 * `volver` (normalmente, al paso anterior). Cada paso nuevo pone su propia
 * entrada, así que «atrás» retrocede un paso cada vez hasta el primero
 * (que pasa `null` y deja salir de la pantalla).
 */
export function useAtrasDePaso(paso: string | null, volver: () => void) {
  const volverRef = useRef(volver);
  useEffect(() => {
    volverRef.current = volver;
  }, [volver]);

  useEffect(() => {
    if (paso == null) {
      return;
    }
    let usada = false;
    const entrada = abrirEntradaAtras(() => {
      usada = true;
      volverRef.current();
    });
    return () => {
      if (!usada) {
        entrada.quitar();
      }
    };
  }, [paso]);
}
